import {
  MutationCache,
  QueryCache,
  QueryClient,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { ApiError, api, unwrap, type Schemas } from './api/client.js';
import { isLanguage, setLanguage } from './i18n.js';

export type Me = {
  user: Schemas['User'];
  account: Schemas['Account'];
  /** mfaPending: the second factor is on and this session has not given it yet (SP-D19). */
  session: { mfaVerified: boolean; mfaPending: boolean };
};

export const ME_KEY = ['me'] as const;

/**
 * The app's query client. Client errors (4xx) are not retried. A request the server refuses for
 * the second factor (an operator idle 30 minutes or 12 hours in, SP-D18; a sign-in without the
 * code yet, SP-D19) reloads the session, so the app asks for the code instead of the error.
 */
export function createQueryClient(): QueryClient {
  const onError = (error: unknown) => {
    if (error instanceof ApiError && error.code === 'MFA_REQUIRED')
      void client.invalidateQueries({ queryKey: ME_KEY });
  };
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError }),
    mutationCache: new MutationCache({ onError }),
    defaultOptions: {
      queries: {
        retry: (count, error) =>
          !(error instanceof ApiError && error.problem.status < 500) && count < 2,
        refetchOnWindowFocus: true,
      },
    },
  });
  return client;
}

/** The signed-in user, or null when there is no session. */
export function useMe() {
  const query = useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<Me | null> => {
      try {
        return (await unwrap(api.GET('/me'))) as Me;
      } catch (error) {
        if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });
  // The user's language wins over the browser's once we know it.
  const locale = query.data?.user.locale;
  useEffect(() => {
    if (isLanguage(locale)) setLanguage(locale);
  }, [locale]);
  return query;
}

export function useSignOut() {
  const client = useQueryClient();
  return async () => {
    await api.POST('/auth/logout').catch(() => undefined);
    client.setQueryData(ME_KEY, null);
    client.clear();
  };
}
